// src/pages/Billing.js
// Minimal-mono billing — pricing (Subscription/Lifetime toggle + connected grid)
// → checkout (real Stripe Elements or Paddle handoff, with redeem-at-checkout)
// → processing → success. Ported 1:1 from the test.html demo, wired to the real
// payment plumbing (Stripe /v1/stripe, Paddle /v1/paddle, lifetime /v1/lifetime,
// DealMirror /v1/dealmirror). Layout is the demo; the money is real.

import { useDispatch, useSelector } from "react-redux";
import { config } from "../config";
import './scss/BillingTable.scss';
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    CardNumberElement, CardExpiryElement, CardCvcElement,
    Elements, useStripe, useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { fetchMembers } from "../redux/Slices/memberSlice";
import { fetchWorkspaces } from "../redux/Slices/workspaceSlice";
import { usePaymentProvider } from "../hooks/usePaymentProvider";
import { initializePaddle } from "../utils/paddle";
import { fetchLifetimeTiers, createLifetimeCheckout } from "../api/lifetimeApi";
import PaddlePaymentForm from "../components/Paddle/PaddlePaymentForm";
import SubscriptionManagement from "../components/Billing/SubscriptionManagement";
import SeatQuantityInput from "../components/Billing/SeatQuantityInput";
import { isWorkspaceAdmin } from "../utils/helpers";
import AccessDenied from "../components/AccessDenied";
import { PLAN_PRICING, PLAN_PRICE_IDS } from "../constants/pricing";

const CheckIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ---- Subscription plans (trimmed, demo-style single feature list) ----
const PLANS = [
    {
        name: 'Free', description: 'For you alone or a small team finding its feet.',
        prices: { monthly: '0', annually: '0' }, sub: { monthly: 'Free forever', annually: 'Free forever' },
        heading: 'Includes', features: ['15 collaborators', 'Up to 15 boards', '512 MB storage', 'Real-time notifications'],
    },
    {
        name: 'Standard', description: 'For teams that have outgrown Free — about $5 a person.',
        prices: { monthly: '4.99', annually: '49.99' }, per: { monthly: '/seat·mo', annually: '/seat·yr' },
        sub: { monthly: 'Billed monthly', annually: 'Billed annually' },
        pricesId: PLAN_PRICE_IDS.Standard,
        heading: 'Everything in Free, plus',
        features: ['Unlimited collaborators & storage', 'Unlimited boards & cards', 'Advanced checklists', 'Pro verified badge'],
    },
    {
        name: 'Premium', featured: true, description: 'Your own brand on it, and analytics you can act on.',
        prices: { monthly: '9.99', annually: '95.99' }, per: { monthly: '/seat·mo', annually: '/seat·yr' },
        sub: { monthly: 'Billed monthly', annually: 'Billed annually' },
        pricesId: PLAN_PRICE_IDS.Premium,
        heading: 'Everything in Standard, plus',
        features: ['Extended activity logs & insights', 'Data export (JSON / CSV)', 'White-label & custom domain', 'Priority support'],
    },
    {
        name: 'Enterprise', description: 'For larger companies that need procurement to sign off.',
        prices: { monthly: 'Custom', annually: 'Custom' }, sub: { monthly: 'Priced by seat count', annually: 'Priced by seat count' },
        cta: 'Contact sales', heading: 'Everything in Premium, plus',
        features: ['Advanced reporting', 'User role management', 'SSO (coming soon)', 'Dedicated support'],
    },
];

const planPricing = PLAN_PRICING;

const upgradeFeatures = [
    'Priority support', 'Extended activity logs', 'Performance insights',
    'White-label branding', 'Custom domain support',
];

// Lifetime marketing copy (seats/storage/price come from the live catalog).
const LIFETIME_COPY = {
    tier1: { was: 129, desc: 'Just enough for a small team — you, a co-founder, a contractor or two.', heading: 'Yours, forever', extra: ['25 boards', 'Basic analytics'] },
    tier2: { was: 299, desc: 'For when the team has doubled and the boards keep multiplying.', heading: 'Everything in Tier 1, plus', extra: ['50 boards', 'Advanced analytics'] },
    tier3: { was: 599, desc: 'The whole thing for a settled team — including your own branding.', heading: 'Everything in Tier 2, plus', extra: ['Unlimited boards', 'White-label & custom domain'] },
};

// DealMirror stacking spec — mirrors dealMirrorController.js.
const MAX_CODES = 6;
const redeemSpec = (n) => {
    if (n <= 0) return { label: '—', seats: 0, storage: 0, wl: false, tier: 'tier1' };
    if (n === 1) return { label: 'Tier 1', seats: 10, storage: 5, wl: false, tier: 'tier1' };
    if (n === 2) return { label: 'Tier 2', seats: 20, storage: 10, wl: false, tier: 'tier2' };
    if (n === 3) return { label: 'Tier 3', seats: 30, storage: 20, wl: true, tier: 'tier3' };
    const extra = n - 3;
    return { label: `Tier 3 + ${extra} extra`, seats: 30 + extra * 10, storage: 20 + extra * 5, wl: true, tier: 'addon' };
};

const getBillingPageState = (info) => {
    if (!info) return 'loading';
    const { planType, status, billing, isPro } = info;
    if (['AppSumo', 'DealMirror'].includes(planType)) return 'lifetime-deal';
    if (status === 'canceled' || status === 'past_due' || billing?.cancelAtPeriodEnd) return 'canceled-or-expiring';
    if (isPro && ['Standard', 'Premium'].includes(planType)) return 'subscribed';
    if (status === 'trialing') return 'subscribed';
    return 'not-subscribed';
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';

// ============================================================
//  Checkout flow — one view at a time: checkout → processing → success
//  Wraps real Stripe Elements; hands off to PaddlePaymentForm for Paddle.
// ============================================================

// Redeem panel (real DealMirror validate + redeem, stackable). Used both inside
// checkout (variant="checkout", shows "← Pay instead") and directly on the
// lifetime pricing page (variant="pricing", the wrapper owns cancel).
function RedeemPanel({ shortId, onClose, onRedeemed, variant = 'checkout' }) {
    const [codes, setCodes] = useState(['']);
    const [states, setStates] = useState(['']);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    const spec = redeemSpec(codes.length);
    const setCode = (i, v) => {
        const next = [...codes]; next[i] = v.toUpperCase(); setCodes(next);
        const s = [...states]; s[i] = ''; setStates(s); setMsg('');
    };
    const addCode = () => { if (codes.length < MAX_CODES) { setCodes([...codes, '']); setStates([...states, '']); } };
    const removeCode = (i) => { setCodes(codes.filter((_, x) => x !== i)); setStates(states.filter((_, x) => x !== i)); };

    const redeem = async () => {
        const trimmed = codes.map((c) => c.trim());
        if (trimmed.some((c) => !c)) { setMsg('Enter every license code.'); return; }
        setBusy(true); setMsg('');
        try {
            const body = { codes: trimmed, selectedTier: spec.tier, workspaceId: shortId };
            const vr = await fetch(`${config.API_URI}/v1/dealmirror/validate-code`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
            });
            const vdata = await vr.json();
            if (!vr.ok || !vdata.valid) {
                setStates(codes.map(() => '✕')); setMsg(vdata.message || 'One or more codes are invalid or already used.');
                setBusy(false); return;
            }
            const rr = await fetch(`${config.API_URI}/v1/dealmirror/redeem`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
            });
            const rdata = await rr.json();
            if (!rr.ok) { setMsg(rdata.message || 'Redeem failed. Please try again.'); setBusy(false); return; }
            setStates(codes.map(() => '✓'));
            onRedeemed(rdata.subscription || spec, codes.length);
        } catch (e) {
            setMsg('Something went wrong. Please try again.');
        } finally { setBusy(false); }
    };

    return (
        <div className="redeem-inline">
            <div className="lbl" style={{ marginTop: 0 }}>License code(s)</div>
            {codes.map((c, i) => (
                <div className="code-row" key={i}>
                    <input className={`ci ${states[i] === '✓' ? 'ok' : ''} ${states[i] === '✕' ? 'bad' : ''}`}
                        value={c} onChange={(e) => setCode(i, e.target.value)}
                        placeholder="e.g. ZBBE-XXXX-XXXX-XXXX" spellCheck={false} autoComplete="off" />
                    <span className="cstate">{states[i]}</span>
                    {codes.length > 1 && <button type="button" className="crm" title="Remove" onClick={() => removeCode(i)}>×</button>}
                </div>
            ))}
            <button type="button" className="add-code" onClick={addCode} disabled={codes.length >= MAX_CODES}>+ Stack another code</button>

            <div className="stack-preview">
                <div className="sp-head">You'll unlock</div>
                <div className="sp-tier">{spec.label}</div>
                <div className="sp-meta">{spec.seats} seats · {spec.storage} GB storage{spec.wl ? ' · White Label' : ''}</div>
            </div>

            {msg && <div className="redeem-msg err show">{msg}</div>}

            <div className="ra">
                {variant === 'checkout' && (
                    <button type="button" className="rbtn rbtn-sec" onClick={onClose} disabled={busy}>← Pay instead</button>
                )}
                <button type="button" className="rbtn rbtn-pri" onClick={redeem} disabled={busy}>{busy ? 'Redeeming…' : 'Redeem & activate'}</button>
            </div>
            <p className="rhelp">1 code = Tier 1 · 2 = Tier 2 · 3 = Tier 3. Stack extra codes for +10 seats &amp; +5 GB each. DealMirror &amp; AppSumo both work.</p>
        </div>
    );
}

// Stripe card fields (mounted inside <Elements>).
function StripeFields() {
    const [focused, setFocused] = useState(null);
    const opts = { style: { base: { fontSize: '13px', color: 'inherit', '::placeholder': { color: '#8a8a82' } } } };
    return (
        <>
            <div className="field">
                <label>Card number</label>
                <div className={`stripe-el ${focused === 'n' ? 'focused' : ''}`}>
                    <CardNumberElement options={opts} onFocus={() => setFocused('n')} onBlur={() => setFocused(null)} />
                </div>
            </div>
            <div className="row2">
                <div className="field"><label>Expiry</label><div className={`stripe-el ${focused === 'e' ? 'focused' : ''}`}><CardExpiryElement options={opts} onFocus={() => setFocused('e')} onBlur={() => setFocused(null)} /></div></div>
                <div className="field"><label>CVC</label><div className={`stripe-el ${focused === 'c' ? 'focused' : ''}`}><CardCvcElement options={opts} onFocus={() => setFocused('c')} onBlur={() => setFocused(null)} /></div></div>
            </div>
        </>
    );
}

// The checkout view — plan/tier summary, seat config (subscription), redeem
// toggle, payment method, order summary, and the real pay action.
// Thin wrapper that reads the Stripe hooks (must run inside <Elements>) and
// hands them to CheckoutView as props. Keeps the hooks out of CheckoutView so
// the Paddle branch can render it without an Elements provider.
function StripeElementsBridge(props) {
    const stripe = useStripe();
    const elements = useElements();
    return <CheckoutView {...props} stripe={stripe} elements={elements} />;
}

function CheckoutView({
    intent, workspace, shortId, user, billingInfo, provider, isPaddle,
    eligibleWorkspaces = [], onBack, onDone, dispatch, navigate, stripe, elements,
}) {
    const isLifetime = intent.type === 'lifetime';
    const isPlanChange = intent.type === 'plan-change';
    const [showWsPicker, setShowWsPicker] = useState(false);

    // Switching the target workspace navigates to that workspace's billing URL
    // so seats / current plan / proration all re-derive cleanly for it.
    const switchWorkspace = (ws) => {
        setShowWsPicker(false);
        if (ws.shortId === shortId) return;
        // Plan-change carries a pre-created payment for THIS workspace, so a
        // switch must start over on the target's billing page.
        navigate(`/w/${ws.shortId}/billing`);
    };

    // Close the workspace dropdown on any outside click.
    useEffect(() => {
        if (!showWsPicker) return;
        const close = (e) => { if (!e.target.closest?.('.ws-picker')) setShowWsPicker(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showWsPicker]);

    const [step, setStep] = useState('checkout');           // checkout | processing | success
    const [showRedeem, setShowRedeem] = useState(false);
    const [plan, setPlan] = useState(intent.plan || 'Premium'); // subscription plan name
    const [additionalSeats, setAdditionalSeats] = useState(0);
    const [error, setError] = useState('');
    const [country] = useState('US');
    const [successMeta, setSuccessMeta] = useState(null);
    const [lifetimeTier, setLifetimeTier] = useState(null); // live tier meta for lifetime
    const [paddleActive, setPaddleActive] = useState(false);
    const [isPaying, setIsPaying] = useState(false);

    const [proratedPricing, setProratedPricing] = useState(null);

    // seat math (subscription)
    const currentSubscription = billingInfo;
    const userCount = billingInfo?.activeMembers || 0;
    const perUserPrice = planPricing[plan]?.[intent.billing] || 0;
    const paidSeats = currentSubscription?.allocatedSeats || 0;
    const isTrialing = currentSubscription?.status === 'trialing';
    const unpaidSeats = (isTrialing || !currentSubscription?.isPro) ? userCount : (currentSubscription?.pendingSeats || 0);
    // Additional (future) seats are prorated for the days left in the period when
    // the backend returns a prorated price; otherwise fall back to full price.
    const userTotal = perUserPrice * unpaidSeats;
    const additionalSeatsTotal = (additionalSeats > 0 && proratedPricing?.proratedPrice != null)
        ? proratedPricing.proratedPrice
        : perUserPrice * additionalSeats;
    const subtotal = isPlanChange
        ? (intent.proration?.amountDollars || 0)
        : isLifetime
            ? (lifetimeTier?.amountDollars || intent.amountDollars || 0)
            : (userTotal + additionalSeatsTotal);
    const total = subtotal;

    // Fetch prorated pricing whenever additionalSeats / plan / cycle change.
    useEffect(() => {
        if (isLifetime || additionalSeats <= 0) { setProratedPricing(null); return; }
        let active = true;
        (async () => {
            try {
                const res = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/calculate-seat-pricing`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                    body: JSON.stringify({ additionalSeats, planType: plan, billingCycle: intent.billing }),
                });
                if (res.ok && active) setProratedPricing(await res.json());
            } catch (e) { /* fall back to full price */ }
        })();
        return () => { active = false; };
    }, [isLifetime, additionalSeats, plan, intent.billing, shortId]);

    // fetch live lifetime tier meta on mount (for accurate price/seats)
    useEffect(() => {
        if (!isLifetime) return;
        let active = true;
        fetchLifetimeTiers().then((d) => {
            if (!active) return;
            const t = (d.tiers || []).find((x) => x.tier === intent.tier);
            if (t) setLifetimeTier(t);
        }).catch(() => {});
        return () => { active = false; };
    }, [isLifetime, intent.tier]);

    const priceId = !isLifetime && PLANS.find((p) => p.name === plan)?.pricesId?.[intent.billing];

    const finishSuccess = (meta) => { setSuccessMeta(meta); setStep('success'); };

    // ---- STRIPE pay (subscription or lifetime) ----
    const payStripe = async () => {
        if (isPaying) return;                     // guard against double-submit
        if (!stripe || !elements) { setError('Payment form is still loading — please retry in a moment.'); return; }
        setIsPaying(true); setError(''); setStep('processing');
        try {
            let clientSecret, billingDetails;
            if (isPlanChange) {
                clientSecret = intent.clientSecret;
                if (!clientSecret) throw new Error('Could not start the plan change. Please try again.');
                billingDetails = { email: user?.user?.email, name: user?.user?.name };
            } else if (isLifetime) {
                const r = await createLifetimeCheckout({ tier: intent.tier, workspaceId: shortId });
                clientSecret = r.clientSecret;
                if (!clientSecret) throw new Error('Could not start the lifetime checkout. Please try again.');
                billingDetails = { email: user?.user?.email, name: user?.user?.name };
            } else {
                if (!workspace?._id) throw new Error('Workspace not loaded yet — please retry in a moment.');
                if (total === 0) { dispatch(fetchWorkspaces()); onDone(); return; }
                const res = await fetch(`${config.API_URI}/v1/stripe/create-payment-intent`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                    body: JSON.stringify({ priceId, planType: plan, userCount: unpaidSeats, additionalSeats, billingCycle: intent.billing, workspaceId: workspace._id }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.clientSecret) {
                    throw new Error(data.error || data.message || 'Could not start the payment. Please try again.');
                }
                clientSecret = data.clientSecret;
                billingDetails = { address: { country }, email: user?.user.email, name: user?.user.name };
            }
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: { card: elements.getElement(CardNumberElement), billing_details: billingDetails },
            });
            if (result.error) throw new Error(result.error.message);
            dispatch(fetchWorkspaces());
            finishSuccess(isPlanChange
                ? { head: `You're on ${plan}`, detail: `Your plan is now ${plan}, billed ${intent.billing === 'annually' ? 'yearly' : 'monthly'}.`, wl: plan === 'Premium' }
                : isLifetime
                    ? { head: `You're on Lifetime ${lifetimeTier?.name || 'plan'}`, detail: `${lifetimeTier?.seats || ''} seats — active now.`, wl: lifetimeTier?.whiteLabel }
                    : { head: `You're on ${plan}`, detail: `${unpaidSeats + additionalSeats} seat${unpaidSeats + additionalSeats > 1 ? 's' : ''} — active now.`, wl: plan === 'Premium' });
        } catch (e) {
            setError(e.message || 'Payment failed. Please try again.'); setStep('checkout');
        } finally {
            setIsPaying(false);
        }
    };

    const onPay = () => {
        // Nothing to charge (e.g. already-Pro with 0 new seats) → just finish,
        // don't try to create a $0 transaction (Paddle returns none → error).
        if (!isLifetime && total === 0) {
            dispatch(fetchWorkspaces());
            finishSuccess({ head: `You're on ${plan}`, detail: 'Your plan is up to date — no charge needed.', wl: plan === 'Premium' });
            return;
        }
        if (isPaddle) { setStep('processing'); setPaddleActive(true); } else { payStripe(); }
    };

    // redeem success -> jump to Done
    const onRedeemed = (sub, count) => {
        dispatch(fetchWorkspaces());
        const label = sub.tier ? sub.tier.replace('tier', 'Tier ') : 'Lifetime';
        finishSuccess({
            head: `${label} activated`,
            detail: `${sub.totalSeats || ''} seats${sub.totalStorage ? ` · ${sub.totalStorage} GB` : ''} — lifetime access is live.`,
            wl: sub.hasWhiteLabel, receipt: `${count} code${count > 1 ? 's' : ''} redeemed. A confirmation is on its way to your email.`,
        });
    };

    // ---------- PROCESSING ----------
    if (step === 'processing' && !paddleActive) {
        return (
            <>
                <div className="center"><div>
                    <div className="spinner" />
                    <h2>Confirming your payment…</h2>
                    <p>Don't close this window — we're activating your plan.</p>
                </div></div>
            </>
        );
    }

    // Paddle: PaddlePaymentForm owns its own summary + inline fields + verify.
    // No outer .co-card here — PaddlePaymentForm renders its own single panel
    // (styled minimal-mono in PaddlePaymentForm.scss) so it's not a box-in-a-box.
    if (paddleActive) {
        return (
            <div className="paddle-pay-wrap">
                    <PaddlePaymentForm
                        type={isLifetime ? 'lifetime' : 'seat'}
                        showSummary={false}
                        createTransaction={
                            isPlanChange
                                ? async () => ({ transactionId: intent.transactionId })
                                : isLifetime
                                    ? () => createLifetimeCheckout({ tier: intent.tier, workspaceId: shortId })
                                    : async () => {
                                        if (!workspace?._id) throw new Error('Workspace not loaded yet — please retry in a moment.');
                                        const res = await fetch(`${config.API_URI}/v1/paddle/create-transaction`, {
                                            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                                            body: JSON.stringify({ planType: plan, userCount: unpaidSeats, additionalSeats, billingCycle: intent.billing, workspaceId: workspace._id }),
                                        });
                                        const data = await res.json().catch(() => ({}));
                                        if (!res.ok) throw new Error(data.error || data.message || 'Could not start the payment. Please try again.');
                                        return data;
                                    }}
                        title={isPlanChange ? `${plan} — ${intent.billing === 'annually' ? 'yearly' : 'monthly'}` : isLifetime ? `${lifetimeTier?.name || 'Lifetime'} — One-time payment` : `${plan} — ${intent.billing}`}
                        amountDollars={isLifetime ? lifetimeTier?.amountDollars : total.toFixed(2)}
                        billingNote={isPlanChange ? 'Prorated difference for the rest of this period' : isLifetime ? 'One-time payment • Lifetime access' : undefined}
                        verifyType={isLifetime ? 'lifetime_purchase' : 'seat_subscription'}
                        workspaceId={shortId}
                        frameTarget={isLifetime ? 'paddle-inline-lifetime' : 'paddle-inline-seat'}
                        successSeats={isLifetime ? lifetimeTier?.seats : (isPlanChange ? undefined : unpaidSeats + additionalSeats)}
                        successEmail={user?.user?.email}
                        onSuccess={() => { dispatch(fetchWorkspaces()); onDone(); }}
                        onCancel={() => { setPaddleActive(false); setStep('checkout'); }}
                    />
            </div>
        );
    }

    // ---------- SUCCESS ----------
    if (step === 'success') {
        return (
            <>
                <div className="done payment-success-wrap">
                    <div className="done-check"><svg viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M5 12.5l4 4 10-11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                    <h2>{successMeta?.head || 'Your plan is active'}</h2>
                    <p className="detail">
                        {successMeta?.detail || ''}
                        {successMeta?.wl && <><br /><span className="pill">White Label</span></>}
                    </p>
                    <p className="receipt">{successMeta?.receipt || `Receipt on its way to ${user?.user?.email || 'your email'}.`}</p>
                    <button className="done-btn" onClick={onDone}>Go to billing</button>
                </div>
            </>
        );
    }

    // ---------- CHECKOUT ----------
    return (
        <>
            <div className="co-head">
                <div className="ws-ic">{(workspace?.name || 'W').charAt(0).toUpperCase()}</div>
                <div className="ws-picker">
                    {eligibleWorkspaces.length > 1 ? (
                        <button type="button" className="ws-picker-trigger" onClick={() => setShowWsPicker((v) => !v)}>
                            <span className="name">{workspace?.name || 'Workspace'}</span>
                            <span className="ws-caret" aria-hidden="true">▾</span>
                        </button>
                    ) : (
                        <div className="name">{workspace?.name || 'Workspace'}</div>
                    )}
                    <div className="vis">Adding plan to this workspace{user?.user?.email ? ` · billed to ${user.user.email}` : ''}</div>

                    {showWsPicker && (
                        <div className="ws-picker-menu">
                            <div className="ws-picker-head">Add the plan to…</div>
                            {eligibleWorkspaces.map((ws) => (
                                <button
                                    type="button"
                                    key={ws.shortId}
                                    className={`ws-picker-item ${ws.shortId === shortId ? 'current' : ''}`}
                                    onClick={() => switchWorkspace(ws)}
                                >
                                    <span className="ws-picker-ic">{(ws.name || 'W').charAt(0).toUpperCase()}</span>
                                    <span className="ws-picker-name">{ws.name}</span>
                                    {ws.shortId === shortId && <span className="ws-picker-check">✓</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <button className="back" style={{ marginLeft: 'auto' }} onClick={onBack}>← Plans</button>
            </div>

            <div className="co-grid">
                <form className="co-card" onSubmit={(e) => e.preventDefault()}>
                    <h3>{isPlanChange ? 'Confirm your plan change' : isLifetime ? 'Confirm your lifetime tier' : 'Choose your plan'}</h3>
                    <p className="hint">{isPlanChange ? 'You’ll only pay the prorated difference for the rest of this period.' : isLifetime ? 'One payment for this workspace — or redeem a code instead.' : 'Select a plan and how many seats you need.'}</p>

                    {/* Redeem prompt — opens the popup (below). Hidden for plan changes. */}
                    {!isPlanChange && (
                        <div className="redeem-prompt">
                            <span>Have a redeem code?</span>
                            <button type="button" className="redeem-link" onClick={() => setShowRedeem(true)}>Redeem a code</button>
                        </div>
                    )}

                    <>
                        {isPlanChange && (
                            <>
                                <div className="srow" style={{ borderTop: 0 }}><span className="k">New plan</span><span className="v">{plan}</span></div>
                                <div className="srow"><span className="k">Billing</span><span className="v">{intent.billing === 'annually' ? 'Yearly' : 'Monthly'}</span></div>
                                <div className="srow"><span className="k">Seats</span><span className="v">{intent.proration?.seats || paidSeats}</span></div>
                                <div className="srow"><span className="k">Days left in period</span><span className="v">{intent.proration?.remainingDays ?? '—'}</span></div>
                            </>
                        )}
                        {!isLifetime && !isPlanChange && (
                            <>
                                    <div className="lbl">Plan</div>
                                    <div className="opts">
                                        {['Standard', 'Premium'].map((name) => (
                                            <div key={name} className={`opt ${plan === name ? 'sel' : ''}`} onClick={() => setPlan(name)}>
                                                <div className="pn">{name}</div>
                                                <div className="pp">${planPricing[name][intent.billing]} <span className="pnote">{intent.billing === 'annually' ? '/seat·yr' : '/seat·mo'}</span></div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="lbl">Seats</div>
                                    <div className="srow"><span className="k">Members in workspace</span><span className="v">{userCount} total</span></div>
                                    <div className="srow"><span className="k">Already paid</span><span className="v">{paidSeats} seats</span></div>
                                    <div className="srow">
                                        <span className="k">Additional seats</span>
                                        <SeatQuantityInput initialValue={0} minValue={0} onChange={setAdditionalSeats} />
                                    </div>
                                </>
                            )}
                            {isLifetime && (
                                <>
                                    <div className="srow" style={{ borderTop: 0 }}><span className="k">Tier</span><span className="v">{lifetimeTier?.name || intent.tier}</span></div>
                                    <div className="srow"><span className="k">Applies to</span><span className="v">{workspace?.name}</span></div>
                                </>
                            )}

                            <div className="lbl">Payment method</div>
                            <div className="pm-tabs">
                                <div className="pm-tab on">Paddle <span className="mini">+ tax</span></div>
                            </div>

                            {isPaddle ? (
                                <div className="paddle-note"><strong>Paddle handles the rest.</strong><br />A secure Paddle overlay opens on pay — card, PayPal, Apple Pay, and local taxes are calculated at checkout.</div>
                            ) : (
                                <StripeFields />
                            )}
                    </>
                </form>

                <div className="co-card sum">
                    <h4>Order summary</h4>
                    {isPlanChange ? (
                        <div className="sline"><span>Switch to {plan} ({intent.billing === 'annually' ? 'yearly' : 'monthly'}) — prorated</span><span className="amt">${subtotal.toFixed(2)}</span></div>
                    ) : isLifetime ? (
                        <div className="sline"><span>Lifetime {lifetimeTier?.name || intent.tier} · {lifetimeTier?.seats || ''} seats</span><span className="amt">${subtotal}</span></div>
                    ) : (
                        <>
                            {unpaidSeats > 0 && (
                                <div className="sline"><span>{plan} × {unpaidSeats} seat{unpaidSeats > 1 ? 's' : ''}</span><span className="amt">${userTotal.toFixed(2)}</span></div>
                            )}
                            {additionalSeats > 0 && (
                                <div className="sline">
                                    <span>+{additionalSeats} seat{additionalSeats > 1 ? 's' : ''}{proratedPricing?.isProrated ? ` (prorated${proratedPricing?.proratedData?.remainingDays ? `, ${proratedPricing.proratedData.remainingDays}d left` : ''})` : ''}</span>
                                    <span className="amt">${additionalSeatsTotal.toFixed(2)}</span>
                                </div>
                            )}
                            {unpaidSeats === 0 && additionalSeats === 0 && (
                                <div className="sline"><span>{plan}</span><span className="amt">$0.00</span></div>
                            )}
                        </>
                    )}
                    {isPaddle && <div className="sline muted"><span>Tax</span><span className="amt">+ calculated at checkout</span></div>}
                    <div className="stot">
                        <div><div className="lab">{isPlanChange ? 'Due now' : 'Total'}</div><div className="cyc">{isPaddle ? 'plus applicable tax' : isPlanChange ? 'prorated difference' : isLifetime ? 'one-time' : (intent.billing === 'annually' ? 'billed yearly' : 'billed monthly')}</div></div>
                        <div className="big">${isLifetime ? subtotal : total.toFixed(2)}{isPaddle ? <span className="plus-tax"> + tax</span> : null}</div>
                    </div>
                    <button className="pay-btn" onClick={onPay}
                        disabled={isPaying || (!isPaddle && (!stripe || !elements)) || (isLifetime && !subtotal)}>
                        {isPaying ? 'Processing…' : isPaddle ? 'Continue to Paddle' : `Pay $${isLifetime ? subtotal : total.toFixed(2)}`}
                    </button>
                    {error && <div className="co-error">{error}</div>}
                    <div className="trust">Secured &amp; encrypted · Cancel anytime</div>
                </div>
            </div>

            {/* Redeem popup (opened by "Have a redeem code?") */}
            {showRedeem && (
                <div className="redeem-modal-overlay" onClick={() => setShowRedeem(false)}>
                    <div className="redeem-modal" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="redeem-modal-close" onClick={() => setShowRedeem(false)}>×</button>
                        <h3 className="redeem-modal-title">Redeem your code</h3>
                        <p className="redeem-modal-sub">Enter the code from your DealMirror or AppSumo purchase.</p>
                        <RedeemPanel shortId={shortId} variant="pricing" onClose={() => setShowRedeem(false)} onRedeemed={onRedeemed} />
                    </div>
                </div>
            )}
        </>
    );
}

// ============================================================
function Billing() {
    const [billingInfo, setBillingInfo] = useState(null);
    const [billing, setBilling] = useState('annually');
    const [searchParams] = useSearchParams();
    // Open the Lifetime tab directly when linked with ?tab=lifetime.
    const [mode, setMode] = useState(searchParams.get('tab') === 'lifetime' ? 'lifetime' : 'recurring'); // recurring | lifetime
    const [checkoutIntent, setCheckoutIntent] = useState(null); // {type,plan|tier,billing,amountDollars}
    // Lifetime owners can buy again to STACK more tiers (cumulative seats/
    // storage) — picked inline, exactly like "Add More Seats" for recurring.
    const [stackTier, setStackTier] = useState('tier3');
    const [additionalSeats, setAdditionalSeats] = useState(1);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [lifetimeTiers, setLifetimeTiers] = useState([]);
    const [stripePromise, setStripePromise] = useState(null);
    const [showRedeem, setShowRedeem] = useState(false);     // redeem-on-pricing (lifetime)
    const [redeemDone, setRedeemDone] = useState(null);      // {head, detail, wl, receipt}
    const [actionError, setActionError] = useState('');      // user-facing error for cancel/reactivate/change-plan
    const [showWsPicker, setShowWsPicker] = useState(false); // plans-page workspace picker

    const { shortId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isPaddle, provider, paddleClientToken, paddleEnvironment, loading: providerLoading } = usePaymentProvider();

    const user = useSelector((s) => s.user.user);
    const workspaces = useSelector((s) => s.workspaces.workspaces);
    const workspace = workspaces?.find((w) => w.shortId === shortId);
    const userId = user?.user?._id;
    const isAdmin = isWorkspaceAdmin(workspace, userId);
    // Workspaces the user can buy billing for (owner/admin) — the checkout lets
    // them switch the target so they're sure which workspace gets the plan.
    const eligibleWorkspaces = (workspaces || []).filter((w) => isWorkspaceAdmin(w, userId));

    useEffect(() => {
        dispatch(fetchMembers({ type: 'workspace', id: shortId }));
        dispatch(fetchMembers({ type: 'guests', id: shortId }));
    }, [dispatch, shortId]);

    // Ensure the workspaces list is loaded so `workspace` (name + _id) resolves
    // for the checkout header and Stripe/Paddle subscription calls.
    useEffect(() => {
        if (!workspaces || workspaces.length === 0) dispatch(fetchWorkspaces());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const [loadError, setLoadError] = useState('');
    const fetchBillingInfo = useCallback(async () => {
        setLoadError('');
        try {
            const r = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/status`, { credentials: 'include' });
            if (r.ok) {
                setBillingInfo(await r.json());
            } else {
                const data = await r.json().catch(() => ({}));
                setLoadError(data.message || data.error || 'We couldn’t load your billing details. Please refresh the page.');
            }
        } catch (e) {
            setLoadError('We couldn’t reach the billing server. Check your connection and refresh.');
        }
    }, [shortId]);

    useEffect(() => { if (shortId) fetchBillingInfo(); }, [shortId, fetchBillingInfo]);

    // Close the plans-page workspace picker on outside click.
    useEffect(() => {
        if (!showWsPicker) return;
        const close = (e) => { if (!e.target.closest?.('.ws-picker')) setShowWsPicker(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showWsPicker]);

    // Load Stripe.js eagerly (checkout's <Elements> needs it). Not gated on the
    // provider fetch — Stripe is the default and loading it is harmless for Paddle.
    useEffect(() => {
        if (!stripePromise && config.STRIPE_PUBLIC_KEY) {
            loadStripe(config.STRIPE_PUBLIC_KEY).then(setStripePromise).catch((e) => console.error('Stripe load failed', e));
        }
    }, [stripePromise]);

    // Lifetime tiers for the pricing grid.
    useEffect(() => {
        fetchLifetimeTiers().then((d) => setLifetimeTiers(d.tiers || [])).catch(() => {});
    }, []);

    // Initialize Paddle.js when Paddle is the provider — REQUIRED before
    // PaddlePaymentForm can open its inline checkout ("Unable to open the payment
    // form" otherwise). Includes the customer id so returning buyers are matched.
    useEffect(() => {
        if (isPaddle && paddleClientToken) {
            const pwCustomer = billingInfo?.paddleCustomerId ? { id: billingInfo.paddleCustomerId } : null;
            initializePaddle(paddleClientToken, paddleEnvironment, pwCustomer).catch((e) => console.error('Paddle init failed:', e));
        }
    }, [isPaddle, paddleClientToken, paddleEnvironment, billingInfo?.paddleCustomerId]);

    const pageState = getBillingPageState(billingInfo);
    const userCount = billingInfo?.activeMembers || 0;

    const openSubscriptionCheckout = (plan) => {
        if (plan.name === 'Enterprise') {
            const subject = encodeURIComponent('Zoobbe Enterprise — sales enquiry');
            const body = encodeURIComponent(
                `Hi Zoobbe team,\n\nWe'd like to discuss Enterprise pricing.\n\n` +
                `Workspace: ${workspace?.name || ''}\nMembers: ${userCount}\nContact: ${user?.user?.email || ''}\n\nThanks!`
            );
            window.location.href = `mailto:sales@zoobbe.com?subject=${subject}&body=${body}`;
            return;
        }
        if (!plan.pricesId) return;
        setCheckoutIntent({ type: 'recurring', plan: plan.name, billing });
    };
    const openLifetimeCheckout = (tier) => setCheckoutIntent({ type: 'lifetime', tier });

    const handleAddSeatsAction = () => {
        const cur = PLANS.find((p) => p.name === billingInfo?.planType);
        if (cur?.pricesId) {
            const cycle = billingInfo?.billingCycle || billing;
            setBilling(cycle);
            setCheckoutIntent({ type: 'recurring', plan: cur.name, billing: cycle });
        }
    };
    // Plan change (upgrade Standard→Premium, and/or switch monthly↔yearly).
    // Calls change-plan for the prorated delta. If a charge is needed we open the
    // checkout in plan-change mode with the pre-created payment; if the delta is
    // zero the plan is already switched server-side, so just refresh.
    const [planChangeBusy, setPlanChangeBusy] = useState(false);
    const doChangePlan = async (targetPlan, targetCycle) => {
        if (planChangeBusy) return;
        setPlanChangeBusy(true);
        setActionError('');
        try {
            const r = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/change-plan`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ planType: targetPlan, billingCycle: targetCycle }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setActionError(data.message || data.error || 'We couldn’t change your plan. Please try again.');
                return;
            }
            if (!data.requiresPayment) {
                fetchBillingInfo(); dispatch(fetchWorkspaces());
                return;
            }
            // Charge needed → hand the pre-created payment to the checkout flow.
            setCheckoutIntent({
                type: 'plan-change',
                plan: targetPlan,
                billing: targetCycle,
                provider: data.provider,
                clientSecret: data.clientSecret,
                transactionId: data.transactionId,
                proration: data.proration,
            });
        } catch (e) {
            setActionError('Something went wrong reaching the server. Check your connection and try again.');
        } finally {
            setPlanChangeBusy(false);
        }
    };
    const handleUpgradeToPremium = () => doChangePlan('Premium', billingInfo?.billingCycle || billing);

    const handleCancelSubscription = async () => {
        setActionError('');
        try {
            setCanceling(true);
            const r = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/cancel-subscription`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ cancelImmediately: false, workspaceId: shortId }),
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok) {
                setShowCancelConfirm(false); fetchBillingInfo();
            } else {
                setActionError(data.message || data.error || 'We couldn’t cancel your subscription. Please try again.');
            }
        } catch (e) {
            setActionError('Something went wrong reaching the server. Check your connection and try again.');
        } finally { setCanceling(false); }
    };

    // Resubscribe. If the plan is only SCHEDULED to cancel (still active), we
    // reactivate for free by clearing the pending cancel. If it has fully lapsed
    // to canceled, there's nothing to reactivate — send them through checkout.
    const [reactivating, setReactivating] = useState(false);
    const handleResubscribe = async () => {
        setActionError('');
        const scheduledToCancel = billingInfo?.billing?.cancelAtPeriodEnd;
        if (!scheduledToCancel) {
            // Fully canceled (or no active period) → fresh checkout on the plan
            // they had, falling back to Premium if it's no longer a paid plan.
            const cur = PLANS.find((p) => p.name === billingInfo?.planType && p.pricesId);
            const planName = cur ? cur.name : 'Premium';
            const cycle = billingInfo?.billingCycle || billing;
            setBilling(cycle);
            setCheckoutIntent({ type: 'recurring', plan: planName, billing: cycle });
            return;
        }
        try {
            setReactivating(true);
            const r = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/reactivate-subscription`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ workspaceId: shortId }),
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok) {
                fetchBillingInfo();
            } else if (data.requiresCheckout) {
                // Race: it lapsed between load and click → fall through to checkout.
                const cur = PLANS.find((p) => p.name === billingInfo?.planType && p.pricesId);
                setCheckoutIntent({ type: 'recurring', plan: cur ? cur.name : 'Premium', billing: billingInfo?.billingCycle || billing });
            } else {
                setActionError(data.message || data.error || 'We couldn’t reactivate your subscription. Please try again.');
            }
        } catch (e) {
            setActionError('Something went wrong reaching the server. Check your connection and try again.');
        } finally {
            setReactivating(false);
        }
    };

    const closeCheckout = () => { setCheckoutIntent(null); fetchBillingInfo(); navigate(`/w/${shortId}/billing`); };

    if (!isAdmin && workspace) {
        return <AccessDenied type="billing" message="Only workspace administrators can access billing and subscription management. Please contact your workspace admin if you need to make changes to the subscription." />;
    }

    // --------- Checkout flow (mounts Stripe Elements) ---------
    if (checkoutIntent) {
        const intent = { ...checkoutIntent, billing: checkoutIntent.billing || billing };
        // CheckoutView calls useStripe()/useElements(), so for the Stripe branch
        // it MUST be rendered inside <Elements>. Only Stripe.js needs to be ready;
        // do NOT hard-block on `workspace` (it can be briefly undefined) — the
        // Stripe subscription call needs workspace._id, guarded at pay time.
        const stripeNotReady = !isPaddle && (providerLoading || !stripePromise);

        const shell = (children) => (
            <div className="billing-container checkout-view">
                <div className="close-button" onClick={closeCheckout}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
                </div>
                {children}
            </div>
        );

        if (stripeNotReady) {
            return shell(<div className="billing-loading">Preparing secure checkout…</div>);
        }

        const commonProps = {
            intent, workspace, shortId, user, billingInfo, provider, isPaddle,
            eligibleWorkspaces,
            onBack: () => setCheckoutIntent(null), onDone: closeCheckout, dispatch, navigate,
        };
        // Stripe hooks live in StripeElementsBridge (rendered inside <Elements>);
        // Paddle never touches them, so it gets stripe/elements = null.
        const view = isPaddle
            ? <CheckoutView {...commonProps} stripe={null} elements={null} />
            : <StripeElementsBridge {...commonProps} />;

        return shell(isPaddle ? view : <Elements stripe={stripePromise}>{view}</Elements>);
    }

    // --------- Pricing / plan views ---------
    const renderToggles = () => (
        <div className="billing-toggles">
            <div className="seg">
                <button className={mode === 'recurring' ? 'on' : ''} onClick={() => setMode('recurring')}>Subscription</button>
                <button className={mode === 'lifetime' ? 'on' : ''} onClick={() => setMode('lifetime')}>Lifetime</button>
            </div>
            {mode === 'recurring' && (
                <div className="seg subtle">
                    <button className={billing === 'monthly' ? 'on' : ''} onClick={() => setBilling('monthly')}>Monthly</button>
                    <button className={billing === 'annually' ? 'on' : ''} onClick={() => setBilling('annually')}>Annually <span className="save">· save 17%</span></button>
                </div>
            )}
        </div>
    );

    const renderSubscriptionGrid = () => (
        <div className="billing-cards">
            {PLANS.map((plan) => {
                // "Current" only when the plan is genuinely entitled (payment
                // confirmed / active trial) — NOT when a plan name was written to
                // an unpaid 'incomplete' record at transaction-creation time.
                const isEntitled = billingInfo && (billingInfo.isPro || billingInfo.status === 'trialing');
                const isCurrent = isEntitled && billingInfo.planType === plan.name;
                const priceVal = plan.prices[billing];
                const numeric = !isNaN(parseFloat(priceVal));
                return (
                    <div className={`card ${plan.name.toLowerCase()} ${plan.featured ? 'pick' : ''}`} key={plan.name}>
                        <div className="plan__name">{plan.name}
                            {isCurrent && <span className="tag">Current</span>}
                            {plan.featured && !isCurrent && <span className="tag">Popular</span>}
                        </div>
                        <div className="plan__price">
                            {numeric && <span className="cur">$</span>}
                            <span className="amt" style={!numeric ? { fontSize: 22 } : undefined}>{numeric ? priceVal : priceVal}</span>
                            {plan.per && <span className="per">{plan.per[billing]}</span>}
                        </div>
                        <div className="plan__sub">{plan.sub[billing]}</div>
                        <p className="plan__desc">{plan.description}</p>
                        <button className={`plan__cta ${plan.featured ? 'plan__cta--solid' : ''}`}
                            onClick={() => plan.pricesId ? openSubscriptionCheckout(plan) : (plan.name === 'Enterprise' ? openSubscriptionCheckout(plan) : null)}
                            disabled={isCurrent || (!plan.pricesId && plan.name === 'Free')}>
                            {isCurrent ? 'Current plan' : (plan.cta || (plan.name === 'Free' ? 'Get started' : `Upgrade to ${plan.name}`))}
                        </button>
                        <div className="feat-head">{plan.heading}</div>
                        <ul className="feats">
                            {plan.features.map((f, i) => (
                                <li key={i}><span className="chk"><CheckIcon /></span><span>{f}</span></li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );

    const renderLifetimeGrid = () => {
        const tiers = lifetimeTiers.length ? lifetimeTiers : ['tier1', 'tier2', 'tier3'].map((t) => ({ tier: t, name: t.replace('tier', 'Tier '), seats: '', storage: '', amountDollars: '', whiteLabel: t === 'tier3' }));
        return (
            <>
                <div className="billing-cards lifetime">
                    {tiers.map((tier) => {
                        const copy = LIFETIME_COPY[tier.tier] || {};
                        const featured = tier.tier === 'tier3';
                        return (
                            <div className={`card ${tier.tier} ${featured ? 'pick' : ''}`} key={tier.tier}>
                                <div className="plan__name">{tier.name}{featured && <span className="tag">Best value</span>}</div>
                                <div className="plan__price"><span className="cur">$</span><span className="amt">{tier.amountDollars}</span>{copy.was && <span className="was">${copy.was}</span>}</div>
                                <div className="plan__sub">One-time payment</div>
                                <p className="plan__desc">{copy.desc}</p>
                                <button className={`plan__cta ${featured ? 'plan__cta--solid' : ''}`} onClick={() => openLifetimeCheckout(tier.tier)}>Buy lifetime</button>
                                <button type="button" className="redeem-under-btn" onClick={() => setShowRedeem(true)}>Redeem a code</button>
                                <div className="feat-head">{copy.heading}</div>
                                <ul className="feats">
                                    <li><span className="chk"><CheckIcon /></span><span><strong>{tier.seats}</strong> team seats</span></li>
                                    <li><span className="chk"><CheckIcon /></span><span>{tier.storage} GB storage</span></li>
                                    {(copy.extra || []).map((f, i) => (
                                        <li key={i}><span className="chk"><CheckIcon /></span><span>{f}</span></li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
                <div className="assure">
                    <div><span className="n">Once</span><span>No renewal, no card kept on file</span></div>
                    <div><span className="n">60d</span><span>Refund window, no questions</span></div>
                    <div><span className="n">∞</span><span>Stack codes to grow — redeem anytime.</span></div>
                </div>
            </>
        );
    };

    // Redeem popup (opened by any "Redeem a code" button).
    const renderRedeemModal = () => {
        if (!showRedeem && !redeemDone) return null;
        return (
            <div className="redeem-modal-overlay" onClick={() => { setShowRedeem(false); setRedeemDone(null); }}>
                <div className="redeem-modal" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="redeem-modal-close" onClick={() => { setShowRedeem(false); setRedeemDone(null); }}>×</button>
                    {redeemDone ? (
                        <div className="done">
                            <div className="done-check"><svg viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M5 12.5l4 4 10-11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                            <h2>{redeemDone.head}</h2>
                            <p className="detail">
                                {redeemDone.detail}
                                {redeemDone.wl && <><br /><span className="pill">White Label</span></>}
                            </p>
                            <p className="receipt">{redeemDone.receipt}</p>
                            <button className="done-btn" onClick={() => setRedeemDone(null)}>Done</button>
                        </div>
                    ) : (
                        <>
                            <h3 className="redeem-modal-title">Redeem your code</h3>
                            <p className="redeem-modal-sub">Enter the code from your DealMirror or AppSumo purchase.</p>
                            <RedeemPanel
                                shortId={shortId}
                                variant="pricing"
                                onClose={() => setShowRedeem(false)}
                                onRedeemed={(sub, count) => {
                                    dispatch(fetchWorkspaces()); fetchBillingInfo();
                                    const label = sub.tier ? sub.tier.replace('tier', 'Tier ') : 'Lifetime';
                                    setRedeemDone({
                                        head: `${label} activated`,
                                        detail: `${sub.totalSeats || ''} seats${sub.totalStorage ? ` · ${sub.totalStorage} GB` : ''} — lifetime access is live.`,
                                        wl: sub.hasWhiteLabel,
                                        receipt: `${count} code${count > 1 ? 's' : ''} redeemed. A confirmation is on its way to your email.`,
                                    });
                                    setShowRedeem(false);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderPricing = () => {
        const rawPlan = billingInfo?.planType || 'Free';
        // DealMirror is the internal name for our lifetime deal — show "Lifetime".
        const wsPlan = rawPlan === 'DealMirror' ? 'Lifetime' : rawPlan;
        const wsEntitled = billingInfo && (billingInfo.isPro || billingInfo.status === 'trialing');
        const wsMembers = billingInfo?.activeMembers ?? 0;
        const wsSeats = billingInfo?.allocatedSeats ?? 0;
        return (
        <div className="pricing-layout">
            {/* Left sidebar — everything about the SELECTED WORKSPACE */}
            <aside className="pricing-aside">
                <div className="ws-select ws-picker">
                    <span className="ws-select-label">Workspace</span>
                    {eligibleWorkspaces.length > 1 ? (
                        <button type="button" className="ws-picker-trigger ws-select-box" onClick={() => setShowWsPicker((v) => !v)}>
                            <span className="ws-select-ic">{(workspace?.name || 'W').charAt(0).toUpperCase()}</span>
                            <span className="ws-select-name">{workspace?.name || 'Workspace'}</span>
                            <span className="ws-caret" aria-hidden="true">▾</span>
                        </button>
                    ) : (
                        <span className="ws-select-box ws-select-static">
                            <span className="ws-select-ic">{(workspace?.name || 'W').charAt(0).toUpperCase()}</span>
                            <span className="ws-select-name">{workspace?.name || 'Workspace'}</span>
                        </span>
                    )}
                    {showWsPicker && (
                        <div className="ws-picker-menu">
                            <div className="ws-picker-head">Choose a workspace</div>
                            {eligibleWorkspaces.map((ws) => (
                                <button
                                    type="button"
                                    key={ws.shortId}
                                    className={`ws-picker-item ${ws.shortId === shortId ? 'current' : ''}`}
                                    onClick={() => { setShowWsPicker(false); if (ws.shortId !== shortId) navigate(`/w/${ws.shortId}/billing`); }}
                                >
                                    <span className="ws-picker-ic">{(ws.name || 'W').charAt(0).toUpperCase()}</span>
                                    <span className="ws-picker-name">{ws.name}</span>
                                    {ws.shortId === shortId && <span className="ws-picker-check">✓</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Current workspace status */}
                <div className="ws-info">
                    <div className="ws-info-row">
                        <span className="ws-info-k">Current plan</span>
                        <span className="ws-info-v">
                            {wsEntitled ? wsPlan : 'Free'}
                            <span className={`ws-badge ${wsEntitled ? 'on' : ''}`}>{wsEntitled ? (billingInfo?.status === 'trialing' ? 'Trial' : 'Active') : 'Free'}</span>
                        </span>
                    </div>
                    <div className="ws-info-row">
                        <span className="ws-info-k">Members</span>
                        <span className="ws-info-v">{wsMembers}{wsSeats ? ` / ${wsSeats} seats` : ''}</span>
                    </div>
                    {wsEntitled && billingInfo?.billingCycle && (
                        <div className="ws-info-row">
                            <span className="ws-info-k">Billing</span>
                            <span className="ws-info-v">{billingInfo.billingCycle === 'annually' ? 'Annual' : billingInfo.billingCycle === 'lifetime' ? 'Lifetime' : 'Monthly'}</span>
                        </div>
                    )}
                </div>
            </aside>

            {/* Right — pricing title, toggles, and the plan cards */}
            <div className="pricing-main">
                <div className="billing-page-header">
                    <h1>{mode === 'lifetime' ? 'Pay once. Keep it forever.' : 'Simple pricing. Pay monthly, or once.'}</h1>
                    <p>{mode === 'lifetime' ? 'Pick a lifetime tier and pay once — no renewals, keep it forever.' : 'Subscribe per seat and scale as you grow — or buy a lifetime deal. Change plans anytime.'}</p>
                </div>
                {renderToggles()}
                {mode === 'recurring' ? renderSubscriptionGrid() : renderLifetimeGrid()}
                {mode === 'recurring' && (
                    <div className="assure">
                        <div><span className="n">Secure</span><span>Payments &amp; tax handled by Paddle</span></div>
                        <div><span className="n">Flexible</span><span>Cancel anytime, no lock-in</span></div>
                        <div><span className="n">USD</span><span>Prorated when you change plans</span></div>
                    </div>
                )}
            </div>

            {renderRedeemModal()}
        </div>
        );
    };

    const renderWarningBanner = () => {
        const isCanceling = billingInfo?.billing?.cancelAtPeriodEnd;
        const isPastDue = billingInfo?.status === 'past_due';
        return (
            <div className={`billing-warning-banner ${isPastDue ? 'error' : ''}`}>
                <div className="banner-text">
                    {isCanceling && <p>Your subscription ends on <strong>{formatDate(billingInfo.billing.currentPeriodEnd)}</strong>. After that, your workspace switches to Free.</p>}
                    {isPastDue && <p>Your payment is past due. Please update your payment method to avoid interruption.</p>}
                    {!isCanceling && !isPastDue && <p>Your subscription has been canceled. Resubscribe to regain premium features.</p>}
                </div>
                <button className="banner-action" onClick={isPastDue ? handleAddSeatsAction : handleResubscribe} disabled={reactivating}>
                    {isPastDue ? 'Update Payment' : reactivating ? 'Reactivating…' : (isCanceling ? 'Keep my plan' : 'Resubscribe')}
                </button>
            </div>
        );
    };

    const renderSubscribedLayout = () => {
        const canUpgrade = billingInfo?.planType === 'Standard';
        const cycle = billingInfo?.billingCycle || 'annually';
        const perSeat = planPricing[billingInfo?.planType]?.[cycle] || 0;
        const label = cycle === 'annually' ? 'yr' : 'mo';
        const allocated = billingInfo?.allocatedSeats || 0;
        const active = billingInfo?.activeMembers || 0;
        const pct = Math.min(100, (active / Math.max(1, allocated)) * 100);
        return (
            <div className="billing-subscribed-content">
                <div className="subscription-panel">
                    <h2>Your Subscription</h2>
                    <SubscriptionManagement billingInfo={billingInfo} shortId={shortId} onUpdate={fetchBillingInfo} />
                    <div className="subscription-actions">
                        <button className="action-btn primary" onClick={handleAddSeatsAction}>Add Seats</button>
                        {canUpgrade && <button className="action-btn" onClick={handleUpgradeToPremium}>Upgrade Plan</button>}
                        <button className="action-btn danger" onClick={() => setShowCancelConfirm(true)}>Cancel Subscription</button>
                    </div>
                    {showCancelConfirm && (
                        <div className="cancel-confirm">
                            <p>Are you sure? Your subscription stays active until the end of the current period.</p>
                            <div className="cancel-confirm-actions">
                                <button className="action-btn danger" onClick={handleCancelSubscription} disabled={canceling}>{canceling ? 'Canceling…' : 'Yes, Cancel'}</button>
                                <button className="action-btn" onClick={() => setShowCancelConfirm(false)}>Keep Subscription</button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="upgrade-panel">
                    {canUpgrade && (
                        <div className="upgrade-card">
                            <h3>Upgrade to Premium</h3>
                            <div className="upgrade-price">${planPricing.Premium[cycle]}/{label} per seat</div>
                            <ul className="upgrade-features">{upgradeFeatures.map((f, i) => <li key={i}><span className="check">✓</span>{f}</li>)}</ul>
                            <button className="upgrade-btn" onClick={handleUpgradeToPremium} disabled={planChangeBusy}>
                                {planChangeBusy ? 'Working…' : 'Upgrade to Premium'}
                            </button>
                        </div>
                    )}
                    {/* Billing-cycle switch — upgrade ONLY (monthly → yearly).
                        Downgrades (yearly → monthly) are not offered. */}
                    {(() => {
                        const curPlan = billingInfo?.planType;
                        if (!['Standard', 'Premium'].includes(curPlan)) return null;
                        // Only monthly subscribers see the switch (to yearly).
                        if (cycle !== 'monthly') return null;
                        return (
                            <div className="cycle-switch-card">
                                <h3>Save with yearly billing</h3>
                                <p className="cycle-switch-note">
                                    You’re billed monthly. Switch to yearly at
                                    {' '}${planPricing[curPlan]?.annually}/yr per seat and save 17%.
                                </p>
                                <button className="cycle-switch-btn" onClick={() => doChangePlan(curPlan, 'annually')} disabled={planChangeBusy}>
                                    {planChangeBusy ? 'Working…' : 'Switch to yearly billing'}
                                </button>
                            </div>
                        );
                    })()}
                    <div className="add-seats-card">
                        <h3>Add More Seats</h3>
                        <div className="seats-progress-container">
                            <div className="seats-progress-bar"><div className="seats-progress-fill" style={{ width: `${pct}%` }} /></div>
                            <span className="seats-label">{active} of {allocated} seats used</span>
                        </div>
                        <SeatQuantityInput initialValue={1} minValue={1} onChange={setAdditionalSeats} />
                        <div className="quick-summary"><p>{additionalSeats} seat{additionalSeats > 1 ? 's' : ''} × ${perSeat}/{label} = ${(perSeat * additionalSeats).toFixed(2)}/{label}</p></div>
                        <button className="add-seats-btn" onClick={handleAddSeatsAction}>Add {additionalSeats} Seat{additionalSeats > 1 ? 's' : ''}</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`billing-container ${['subscribed', 'lifetime-deal'].includes(pageState) ? 'subscribed-layout' : ''}`}>
            <div className="close-button" onClick={() => navigate('/')}>
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
            </div>

            {/* Action error banner — cancel / reactivate / change-plan failures. */}
            {actionError && (
                <div className="billing-action-error" role="alert">
                    <span>{actionError}</span>
                    <button type="button" className="dismiss" aria-label="Dismiss" onClick={() => setActionError('')}>×</button>
                </div>
            )}

            {pageState === 'loading' && !loadError && <div className="billing-loading">Loading billing information…</div>}

            {loadError && (
                <div className="billing-load-error" role="alert">
                    <p>{loadError}</p>
                    <button type="button" onClick={fetchBillingInfo}>Try again</button>
                </div>
            )}

            {pageState === 'not-subscribed' && renderPricing()}

            {pageState === 'subscribed' && renderSubscribedLayout()}

            {pageState === 'canceled-or-expiring' && (<>{renderWarningBanner()}{renderPricing()}</>)}

            {pageState === 'lifetime-deal' && (() => {
                // Same two-column subscribed layout as recurring: plan on the
                // left, an inline "Add another tier" panel on the right (mirrors
                // "Add More Seats"). No pricing-grid detour.
                const selected = lifetimeTiers.find((t) => t.tier === stackTier) || lifetimeTiers[lifetimeTiers.length - 1];
                return (
                    <div className="billing-subscribed-content">
                        <div className="subscription-panel">
                            <h2>Your Subscription</h2>
                            <SubscriptionManagement billingInfo={billingInfo} shortId={shortId} onUpdate={fetchBillingInfo} />
                        </div>
                        <div className="upgrade-panel">
                            <div className="add-seats-card">
                                <h3>Add another tier</h3>
                                <p className="cycle-switch-note">Stacks on top of your lifetime — more seats &amp; storage, one-time payment, no renewals.</p>
                                <div className="tier-choices">
                                    {lifetimeTiers.map((t) => (
                                        <button
                                            key={t.tier}
                                            type="button"
                                            className={`tier-choice ${stackTier === t.tier ? 'sel' : ''}`}
                                            onClick={() => setStackTier(t.tier)}
                                        >
                                            <span className="tier-choice-name">{t.name}</span>
                                            <span className="tier-choice-meta">+{t.seats} seats · +{t.storage}GB</span>
                                            <span className="tier-choice-price">${t.amountDollars}</span>
                                        </button>
                                    ))}
                                </div>
                                {selected && (
                                    <>
                                        <div className="quick-summary"><p>{selected.name} · +{selected.seats} seats · +{selected.storage}GB = ${selected.amountDollars} one-time</p></div>
                                        <button className="add-seats-btn" onClick={() => openLifetimeCheckout(selected.tier)}>Buy {selected.name}</button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default Billing;
